/**
 * Vihjed: what the scan autopilot found, as a week's worth of calls.
 *
 * The call queue and nothing else. This tab answers "who do we call"; the
 * Skannid tab answers "what is this site", and the two have different rhythms -
 * one is used while talking to a prospect, the other while deciding which
 * prospect to talk to. That is why they stay two tabs of Kliendiotsing with two
 * filter bars, and why the three engine cards that used to sit above this list
 * moved away (plan step 3): the scan autopilot and the do-not-scan list went to
 * Skannid, next to the scans they make and stop, and the lead watch became the
 * Rikked tab, where its incidents are. Three cards of engine settings used to
 * sit between the page head and the first lead, on the one screen whose job is
 * to start the calls. What stayed is a busy read of the autopilot, so rows
 * still stream in during a run and the toolbar can say why the list is moving.
 *
 * The filter chips stay chips rather than becoming `PillTabs`: severity and
 * status are both multi-select here (you can ask for hot AND warm), and a tab
 * row means one of N. They are the CRM's shared `ToggleChip`; this file and the
 * audits desk each used to carry a private copy of the same pill.
 *
 * No head of its own. The page's head carries the title and this tab's
 * sentence, and the CSV export that used to sit in this tab's head is at the
 * end of the toolbar, beside the search whose results it downloads.
 */
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Download, Loader2, RotateCcw, Target, X } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  dismissLead, leadsCsvUrl, pushLeadToCrm, updateLead,
  LEAD_SEVERITIES, LEAD_STATUSES, type LeadSeverity, type LeadStatus,
} from "../../../lib/scanLeads";
import { Btn, IconBtn, SearchField, ToggleChip, Toolbar, ToolbarGap } from "../../ui";
import { QueueBar, useQueuePicks } from "../../telephony/queue";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { ScanDetailDialog } from "../site-scanner/ScanDetailDialog";
import { BrainDialog } from "../ariregister/brain";
import { stepPath } from "../prospecting/steps";
import { hasActiveLeadFilters } from "./leadFilters";
import { LeadsTable } from "./LeadsTable";
import { useScanLeads } from "./useScanLeads";

export function ScanLeadsSection({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, "manage:ariregister");
  const leads = useScanLeads();
  // This page exists to answer "who do we call"; the selection is how that
  // answer becomes a morning's work rather than a list to copy numbers out of.
  const picks = useQueuePicks();
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState<string | null>(null);
  /**
   * The domain whose scan report is open over the list.
   *
   * A dialog rather than a jump to the scanner page: this page keeps its
   * filters, its sort and its page number in component state, so leaving it
   * throws away the exact selection the operator built to decide who to call.
   */
  const [scanFor, setScanFor] = useState<string | null>(null);
  /** Domain whose development brain is open over the list, or null. */
  const [brainFor, setBrainFor] = useState<string | null>(null);

  const counts = leads.data?.counts;

  const setStatus = useCallback(
    async (domain: string, status: LeadStatus) => {
      try {
        const updated = await updateLead(domain, { status });
        leads.replaceLead(updated);
      } catch {
        setNotice(t("leads.error.save"));
      }
    },
    [leads, t],
  );

  const toCrm = useCallback(
    async (domain: string) => {
      try {
        const result = await pushLeadToCrm(domain);
        setNotice(t(result.created ? "leads.notice.pushed" : "leads.notice.alreadyPushed"));
        leads.refresh();
      } catch {
        setNotice(t("leads.error.push"));
      }
    },
    [leads, t],
  );

  // Was `window.confirm`, which is the browser's dialog rather than the app's -
  // and the one destructive question on this page had no business looking like
  // it came from somewhere else.
  const dismiss = useCallback(
    async (domain: string) => {
      try {
        await dismissLead(domain);
        leads.removeLead(domain);
      } catch {
        setNotice(t("leads.error.dismiss"));
      } finally {
        setConfirmDismiss(null);
      }
    },
    [leads, t],
  );

  const toggleStatus = (status: LeadStatus) =>
    leads.patchFilters({
      status: leads.filters.status.includes(status)
        ? leads.filters.status.filter((s) => s !== status)
        : [...leads.filters.status, status],
    });

  const toggleSeverity = (severity: LeadSeverity) =>
    leads.patchFilters({
      severity: leads.filters.severity.includes(severity)
        ? leads.filters.severity.filter((s) => s !== severity)
        : [...leads.filters.severity, severity],
    });

  const active = hasActiveLeadFilters(leads.filters);

  /**
   * One empty state at a time. The queue used to print "no leads yet, switch
   * the autopilot on" and "nothing matches these filters" together, so a
   * narrowed list told the operator the tool was empty when it was full, and an
   * empty one told them to clear filters they never set. The autopilot hint
   * links to where the autopilot lives now, since its strip left this tab.
   */
  const empty = active
    ? {
        title: t("leads.queue.emptyFiltered"),
        body: t("leads.queue.emptyFilteredBody"),
        action: (
          <Btn small onClick={leads.reset}>
            <RotateCcw className="size-3.5" />
            {t("scanner.table.resetFilters")}
          </Btn>
        ),
      }
    : {
        title: t("leads.queue.empty"),
        body: t("leads.queue.emptyBody"),
        action: (
          <Link to={stepPath("scans")} className="crm-btn">
            <Bot className="size-4" />
            {t("leads.queue.openAutopilot")}
          </Link>
        ),
      };

  return (
    // One open menu at a time across every row.
    <PopHost>
      {notice ? (
        <div className="crm-card mb-3.5 flex items-center gap-2.5 p-3.5">
          <Target className="crm-mut size-4 shrink-0" />
          <p className="crm-sub min-w-0 flex-1" role="status">
            {notice}
          </p>
          {/* A cross, not the circular arrow it used to wear: this closes the
              notice, it does not undo or reload anything. */}
          <IconBtn small onClick={() => setNotice(null)} aria-label={t("common.close")} title={t("common.close")}>
            <X className="size-3.5" />
          </IconBtn>
        </div>
      ) : null}

      <Toolbar>
        <SearchField
          value={leads.qInput}
          onChange={leads.setQInput}
          placeholder={t("leads.searchPlaceholder")}
        />
        <span className="crm-sub crm-tnum px-1.5">
          {t("scanner.table.count", { count: leads.data?.total ?? 0 })}
        </span>
        {/* The autopilot's strip is on the Skannid tab now, so this is the only
            sign here that a run is going - and the reason rows keep arriving
            while nobody touches the page. */}
        {leads.autopilotBusy ? (
          <span className="crm-sub inline-flex items-center gap-1.5 px-1.5" role="status">
            <Loader2 className="size-3.5 animate-spin" />
            {t("leads.queue.running")}
          </span>
        ) : null}
        <ToolbarGap />
        {active ? (
          <Btn small onClick={leads.reset}>
            <RotateCcw className="size-3.5" />
            {t("scanner.table.resetFilters")}
          </Btn>
        ) : null}
        {/* A link rather than a button: the server streams the file, and the
            address carries the same filters the list below was read with. */}
        <a href={leadsCsvUrl(leads.params)} download className="crm-btn crm-btn-sm">
          <Download className="size-4" />
          {t("scanner.table.export")}
        </a>
      </Toolbar>

      <div className="mb-3.5 flex flex-wrap items-center gap-1.5 px-1.5">
        {LEAD_SEVERITIES.map((severity) => (
          <ToggleChip
            key={severity}
            active={leads.filters.severity.includes(severity)}
            onClick={() => toggleSeverity(severity)}
            count={counts?.severity[severity]}
          >
            {t(`leads.severity.${severity}`)}
          </ToggleChip>
        ))}

        {/* The rules that separate severity from status from the flags. Gone on
            a phone: the row wraps into five lines there, so a rule no longer
            marks a boundary - it just lands wherever the wrap put it, and twice
            out of three it was left hanging alone at the end of a line. */}
        <span className="mx-1 hidden h-4 w-px bg-[var(--crm-hair)] sm:block" aria-hidden />

        {LEAD_STATUSES.map((status) => (
          <ToggleChip
            key={status}
            active={leads.filters.status.includes(status)}
            onClick={() => toggleStatus(status)}
            count={counts?.status[status]}
          >
            {t(`leads.status.${status}`)}
          </ToggleChip>
        ))}

        {/* Same rule as above, between status and the flags. */}
        <span className="mx-1 hidden h-4 w-px bg-[var(--crm-hair)] sm:block" aria-hidden />

        <ToggleChip
          active={leads.filters.hasEmail}
          onClick={() => leads.patchFilters({ hasEmail: !leads.filters.hasEmail })}
        >
          {t("scanner.table.filter.hasEmail")}
        </ToggleChip>
        <ToggleChip
          active={leads.filters.hasCompany}
          onClick={() => leads.patchFilters({ hasCompany: !leads.filters.hasCompany })}
        >
          {t("scanner.table.filter.hasCompany")}
        </ToggleChip>
      </div>

      <QueueBar picks={picks} />

      <LeadsTable
        items={leads.data?.items ?? []}
        loading={leads.loading}
        total={leads.data?.total ?? 0}
        page={leads.page}
        pageSize={leads.pageSize}
        sort={leads.sort}
        order={leads.order}
        onSort={leads.toggleSort}
        onPage={leads.setPage}
        onOpenScan={setScanFor}
        onOpenBrain={setBrainFor}
        onStatus={(domain, status) => void setStatus(domain, status)}
        onPushToCrm={(domain) => void toCrm(domain)}
        onDismiss={setConfirmDismiss}
        canManage={canManage}
        picks={picks}
        empty={empty}
        t={t}
      />

      {scanFor ? <ScanDetailDialog domain={scanFor} onClose={() => setScanFor(null)} t={t} /> : null}

      {/* Over the page rather than a route, for the same reason the scan
          report is: the filters, the sort and the page number live in this
          component's state, so navigating away throws the call list away. */}
      {brainFor ? (
        <BrainDialog domain={brainFor} canManage={canManage} me={user.id} onClose={() => setBrainFor(null)} t={t} />
      ) : null}

      {confirmDismiss ? (
        <ConfirmModal
          title={t("leads.action.dismiss")}
          body={t("leads.dismissConfirm", { domain: confirmDismiss })}
          confirmLabel={t("leads.action.dismiss")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => dismiss(confirmDismiss)}
          onClose={() => setConfirmDismiss(null)}
        />
      ) : null}
    </PopHost>
  );
}
